import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setUnseenExportCount } from "Redux/Slices/Common";
import { getReportJobs } from "Explore/Services/Report";
import { logout } from "Explore/Services/Auth";

import {
  LayoutDashboard,
  BarChart2,
  Users,
  Compass,
  FileDown,
  ChevronRight,
  Settings,
  HelpCircle,
  Search,
  MoreHorizontal,
  Building2,
  GraduationCap,
  Library,
  BookOpen,
  Mail,
  PanelLeftClose,
  PanelLeft,
  Bot,
  Server,
  GitCompare,
  FileText,
} from "lucide-react";

import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { ScrollArea } from "components/ui/scroll-area";
import { Separator } from "components/ui/separator";
import { Sheet, SheetContent } from "components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "components/ui/collapsible";

import logoPure from "Explore/Assets/logo-pure.png";

// ─── Navigation definition ───────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    title: "Admin tools",
    items: [
      { key: "overview", label: "Overview", icon: LayoutDashboard, to: "/explore/overview" },
      { key: "assistant", label: "Assistant", icon: Bot, to: "/explore/assistant" },
      { key: "collaborations", label: "Collaboration", icon: Users, to: "/explore/collaborations" },
      {
        key: "benchmarking_group",
        label: "Benchmarking",
        icon: BarChart2,
        children: [
          { key: "analysis", label: "Analysis", icon: BarChart2, to: "/explore/analysis" },
          { key: "peer", label: "Peer", icon: GitCompare, to: "/explore/peer" },
          { key: "report-detail", label: "Report Detail", icon: FileText, to: "/explore/report-detail" },
        ],
      },
      { key: "export-center", label: "My Library", icon: FileDown, to: "/explore/my-library" },
    ]
  },
  {
    title: "Explore",
    items: [
      { key: "faculty", label: "Faculty", icon: GraduationCap, to: "/explore/faculties" },
      { key: "institution", label: "Institution", icon: Building2, to: "/explore/institutions" },
      { key: "unit", label: "Unit", icon: Library, to: "/explore/units" },
      { key: "venue", label: "Venue", icon: BookOpen, to: "/explore/venues" },
    ]
  }
];

const BOTTOM_NAV_ITEMS = [
  { key: "mcp-server", label: "MCP Server", icon: Server, to: "/explore/mcp-server" },
  { key: "settings", label: "Settings", icon: Settings, to: "/explore/settings" },
  { key: "help", label: "Get Help", icon: HelpCircle, to: "/explore/help" },
];

// ─── Helper: find which keys are active ───────────────────────────────────────
const getActiveKeys = (pathname) => {
  const active = { itemKey: null, parentKey: null };
  const allItems = [...NAV_SECTIONS.flatMap(s => s.items), ...BOTTOM_NAV_ITEMS];

  for (const item of allItems) {
    if (item.to && pathname.startsWith(item.to)) {
      active.itemKey = item.key;
      return active;
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.to && pathname.startsWith(child.to)) {
          active.itemKey = child.key;
          active.parentKey = item.key;
          return active;
        }
      }
    }
  }
  return active;
};

// ─── Main Sidebar Component ──────────────────────────────────────────────────
const SubscribedSidebar = ({ collapsed = false, onToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const unseenExportCount = useSelector(state => state.commonState?.unseenExportCount || 0);
  const user = useSelector(state => state.user?.data);
  const currentUserId = user?.id || user?._id || user?.username;

  useEffect(() => {
    if (currentUserId) {
      getReportJobs(currentUserId).then(jobs => {
        const jobsArray = Array.isArray(jobs) ? jobs : (jobs.items || []);
        const count = jobsArray.filter(j => j.is_seen === false).length;
        dispatch(setUnseenExportCount(count));
      }).catch(console.error);
    }
  }, [currentUserId, dispatch]);

  const { itemKey: activeItemKey, parentKey: activeParentKey } = getActiveKeys(location.pathname);

  const handleNavigate = (to) => {
    if (to) {
      navigate(to);
      window.scrollTo(0, 0);
      if (isMobile && onToggle) onToggle(); // Close drawer on mobile
    }
  };

  // ─── Sidebar Content ───
  const SidebarContent = () => (
    <div className="flex h-full w-full flex-col bg-white text-gray-700">
      {/* Header */}
      <div className={cn("flex h-[60px] items-center shrink-0", collapsed ? "justify-center px-0" : "px-4")}>
        {!collapsed && (
          <div className="flex items-center flex-1 gap-2 overflow-hidden cursor-pointer" onClick={() => handleNavigate("/explore/overview")}>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-gray-200">
              <img src={logoPure} alt="Logo" className="h-5 w-5 object-contain" />
            </div>
            <span className="font-semibold text-gray-900 truncate">Academic Online</span>
          </div>
        )}

        {collapsed ? (
          <Button
            variant="ghost"
            size="icon"
            className="group mx-auto hover:bg-gray-100"
            onClick={onToggle}
          >
            <img src={logoPure} alt="Logo" className="h-5 w-5 object-contain block group-hover:hidden" />
            <PanelLeft size={18} className="text-gray-500 hidden group-hover:block" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            onClick={onToggle}
          >
            <PanelLeftClose size={18} />
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 px-3 py-2">
        <div className="flex flex-col gap-6">

          {NAV_SECTIONS.map((section, idx) => (
            <div key={idx} className="flex flex-col gap-1">
              {!collapsed && (
                <span className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 mt-1">
                  {section.title}
                </span>
              )}

              <TooltipProvider delayDuration={0}>
                {section.items.map((item) => {
                  const Icon = item.icon;

                  // Flat Item
                  if (!item.children) {
                    const isActive = activeItemKey === item.key;
                    const navButton = (
                      <Button
                        key={item.key}
                        id={item.key === "export-center" ? "sidebar-export-center-link" : undefined}
                        variant={isActive ? "secondary" : "ghost"}
                        className={cn(
                          "w-full justify-start gap-3 h-9",
                          isActive ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900",
                          collapsed && "justify-center px-0"
                        )}
                        onClick={() => handleNavigate(item.to)}
                      >
                        <div className="relative">
                          <Icon size={18} />
                          {item.key === "export-center" && unseenExportCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[8px] text-white">
                              {unseenExportCount}
                            </span>
                          )}
                        </div>
                        {!collapsed && <span>{item.label}</span>}
                      </Button>
                    );

                    return collapsed ? (
                      <Tooltip key={item.key}>
                        <TooltipTrigger asChild>{navButton}</TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    ) : navButton;
                  }

                  // Collapsible Group
                  const isGroupActive = activeParentKey === item.key;
                  if (collapsed) {
                    return (
                      <Tooltip key={item.key}>
                        <TooltipTrigger asChild>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant={isGroupActive ? "secondary" : "ghost"}
                                className={cn(
                                  "w-full justify-center px-0 h-9",
                                  isGroupActive ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                )}
                              >
                                <Icon size={18} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" align="start" className="w-48 bg-white border-gray-200 text-gray-700">
                              <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
                              <DropdownMenuSeparator className="bg-gray-100" />
                              {item.children.map((child) => (
                                <DropdownMenuItem
                                  key={child.key}
                                  onClick={() => handleNavigate(child.to)}
                                  className={cn("cursor-pointer focus:bg-gray-100 focus:text-gray-900", activeItemKey === child.key && "bg-gray-100 text-gray-900 font-medium")}
                                >
                                  {child.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  return (
                    <Collapsible key={item.key} defaultOpen={isGroupActive} className="w-full">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            "w-full justify-between h-9 text-gray-500 hover:bg-gray-100 hover:text-gray-900",
                            isGroupActive && "bg-gray-50/50"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Icon size={18} />
                            <span>{item.label}</span>
                          </div>
                          <ChevronRight size={16} className="transition-transform duration-200 ui-expanded:rotate-90" />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1 pt-1 pb-2">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = activeItemKey === child.key;
                          return (
                            <Button
                              key={child.key}
                              variant="ghost"
                              className={cn(
                                "w-full justify-start h-8 pl-10 gap-3 text-sm",
                                isChildActive ? "text-gray-900 font-medium bg-gray-50" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50/50"
                              )}
                              onClick={() => handleNavigate(child.to)}
                            >
                              <ChildIcon size={16} />
                              {child.label}
                            </Button>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </TooltipProvider>
            </div>
          ))}

        </div>
      </ScrollArea>

      {/* Bottom Actions */}
      <div className="mt-auto flex flex-col gap-1 p-3">
        <TooltipProvider delayDuration={0}>
          {BOTTOM_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeItemKey === item.key;
            const btn = (
              <Button
                key={item.key}
                variant={isActive ? "secondary" : "ghost"}
                onClick={() => item.to && handleNavigate(item.to)}
                className={cn(
                  "w-full justify-start gap-3 h-9 transition-colors",
                  isActive
                    ? "bg-gray-100 text-gray-900 font-medium"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900",
                  collapsed && "justify-center px-0"
                )}
              >
                <Icon size={18} />
                {!collapsed && <span>{item.label}</span>}
              </Button>
            );
            return collapsed ? (
              <Tooltip key={item.key}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : btn;
          })}
        </TooltipProvider>

        <Separator className="my-2 bg-gray-100" />

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full h-auto p-1.5 justify-start gap-3 hover:bg-gray-100">
              <Avatar className="h-8 w-8 rounded-md border border-gray-200">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-gray-100 text-gray-700 rounded-md">
                  {(user?.full_name || user?.name || user?.first_name || 'U')[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="flex flex-col items-start text-left flex-1 overflow-hidden">
                  <span className="text-sm font-medium text-gray-700 truncate w-full">
                    {user?.full_name || user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || 'User'}
                  </span>
                  <span className="text-xs text-gray-400 truncate w-full">
                    {user?.email || 'user@example.com'}
                  </span>
                </div>
              )}
              {!collapsed && <MoreHorizontal size={16} className="text-gray-400" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side={collapsed ? "right" : "bottom"} className="w-56 bg-white border-gray-200 text-gray-700">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-100" />
            <DropdownMenuItem
              className="focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
              onClick={() => handleNavigate("/explore/settings?tab=profile")}
            >
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              className="focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
              onClick={() => handleNavigate("/explore/settings?tab=account")}
            >
              Account & Plan
            </DropdownMenuItem>
            <DropdownMenuItem
              className="focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
              onClick={() => handleNavigate("/explore/settings")}
            >
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-100" />
            <DropdownMenuItem className="focus:bg-gray-100 focus:text-gray-900 text-red-500 cursor-pointer" onClick={() => logout()}>Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  // ── Mobile Drawer ──
  if (isMobile) {
    return (
      <Sheet open={!collapsed} onOpenChange={(open) => !open && onToggle && onToggle()}>
        <SheetContent side="left" className="p-0 w-[280px] bg-white border-gray-200 border-r">
          <SidebarContent />
        </SheetContent>
      </Sheet>
    );
  }

  // ── Desktop Sidebar ──
  return (
    <aside
      className={cn(
        "h-screen flex-shrink-0 border-r border-gray-200 transition-all duration-300 ease-in-out hidden md:block",
        collapsed ? "w-[72px]" : "w-[260px]"
      )}
    >
      <SidebarContent />
    </aside>
  );
};

export default SubscribedSidebar;
